import './styles/App.css';
import "leaflet-draw/dist/leaflet.draw.css";
import AppRouter from './components/AppRouter';
import AuthErrorPage from './pages/AuthErrorPage';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Riple } from 'react-loading-indicators'
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { preloadFont, clearTextMeasurementCache } from './utils/textMeasurement';
// import { clearPreprocessingCache } from './utils/preprocessing';
import tokenManager from './utils/tokenManager';
import TasksSync from './components/TasksSync';
import { useDataManager } from './utils/useDataManager';
import { CircularProgress } from '@mui/material';
import { APP_VERSION } from './constants/version';

// Функция для установки правильной высоты viewport на мобильных устройствах
export const setViewportHeight = () => {
  // Получаем реальную высоту viewport
  const vh = window.innerHeight * 0.01;

  // Устанавливаем CSS переменную для использования в стилях
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

  // Обновляем также классическую vh переменную
  document.documentElement.style.setProperty('--vh', `${vh}px`);

  // Для мобильных устройств дополнительно проверяем safe area и navbar
  const isMobile = window.innerWidth <= 768;
  // const navbarHeight = isMobile ? 48 : 56;
  // const navbarHeight = document.getElementById('NavBar')?.offsetHeight || (isMobile ? 36 : 40)
  // console.log(document.getElementById('NavBar')?.offsetHeight)
  // // Устанавливаем высоту navbar
  // document.documentElement.style.setProperty('--navbar-height', `${navbarHeight}px`);

  if (isMobile) {
    // Получаем значение safe-area-inset-bottom
    // const computedStyle = getComputedStyle(document.documentElement);
    // const safeAreaBottom = computedStyle.getPropertyValue('--safe-area-bottom') || '0px';

    // console.log(`📱 Мобильное устройство: viewport ${window.innerHeight}px, navbar: ${navbarHeight}px, safe-area-bottom: ${safeAreaBottom}`);

    // Для iOS Safari и других мобильных браузеров
    if (/iPhone|iPad|iPod|iOS|Android/i.test(navigator.userAgent)) {
      // Принудительно устанавливаем высоту без учета адресной строки
      const realHeight = window.screen.height;
      const currentHeight = window.innerHeight;

      if (Math.abs(realHeight - currentHeight) > 100) {
        // Если разница большая, значит адресная строка видна
        document.documentElement.style.setProperty('--viewport-height', `${currentHeight}px`);
      }
    }
  }
};

// Функция для обработки изменения ориентации устройства
const handleOrientationChange = () => {
  // Небольшая задержка для корректного расчета размеров после поворота
  setTimeout(() => {
    setViewportHeight();
  }, 100);
};

const clearCacheData = () => {
  caches.keys().then((names) => {
    names.forEach((name) => {
      caches.delete(name);
    });
  });
  // alert("Complete Cache Cleared");
};

const App = () => {
  // const dispatch = useDispatch()
  const auth = useAuth()
  const authError = useSelector(state => state.state.authError)
  const [hasTriedSignin, setHasTriedSignin] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('version') !== APP_VERSION) {
      localStorage.clear()
      clearCacheData()
      localStorage.setItem('version', APP_VERSION)
    }




  }, []);

  // Используем ref для стабильного доступа к auth
  // const authRef = useRef(auth);
  // authRef.current = auth;

  // Используем новый хук для управления данными
  useDataManager(auth);

  // Устанавливаем правильную высоту viewport при загрузке
  useEffect(() => {
    setViewportHeight();

    // Слушаем изменения размера окна
    window.addEventListener('resize', setViewportHeight);

    // Слушаем изменения ориентации (для мобильных устройств)
    window.addEventListener('orientationchange', handleOrientationChange);

    // Дополнительная обработка для iOS
    window.addEventListener('scroll', setViewportHeight);

    // Используем Visual Viewport API для более точного отслеживания на мобильных
    if (window.visualViewport) {
      const handleVisualViewportChange = () => {
        const isMobile = window.innerWidth <= 768;
        // const navbarHeight = isMobile ? 48 : 56;
        // const navbarHeight = document.getElementById('NavBar')?.offsetHeight || (isMobile ? 40 : 40)
        // console.log(`📱 Visual Viewport изменился: ${window.visualViewport.height}px, navbar: ${navbarHeight}px`);
        document.documentElement.style.setProperty('--viewport-height', `${window.visualViewport.height}px`);
        // document.documentElement.style.setProperty('--navbar-height', `${navbarHeight}px`);
      };

      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      window.visualViewport.addEventListener('scroll', handleVisualViewportChange);

      // Очистка для Visual Viewport API
      return () => {
        window.removeEventListener('resize', setViewportHeight);
        window.removeEventListener('orientationchange', handleOrientationChange);
        window.removeEventListener('scroll', setViewportHeight);

        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
          window.visualViewport.removeEventListener('scroll', handleVisualViewportChange);
        }
      };
    }

    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('scroll', setViewportHeight);
    };
  }, []);

  // Предварительная загрузка шрифтов для оптимизации
  useEffect(() => {
    preloadFont();
  }, []);

  // Обработчик изменения размера окна для очистки кэшей
  useEffect(() => {
    const handleResize = () => {
      clearTextMeasurementCache();
      // clearPreprocessingCache();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {

    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
    if (auth.error) {
      console.error('❌ Ошибка авторизации:', auth.error);
      auth.signinRedirect();
      setHasTriedSignin(true);
    }

  }, [auth.isAuthenticated, auth.activeNavigator, auth.isLoading, hasTriedSignin, hasAuthParams, auth.error]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      // Устанавливаем контекст авторизации и токен в tokenManager
      tokenManager.setAuthContext(auth);
      tokenManager.setCurrentToken(auth.user.access_token);

      const tokenInfo = tokenManager.getTokenInfo(auth.user);
      console.log('🔑 Токен доступа получен');
      console.log('⏰ Токен истекает:', tokenInfo.expiryTime);
      console.log('📊 Статус токена:', tokenInfo.status);
    }
  }, [auth.user])



  // Обработчик событий от silent renew iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'SILENT_RENEW_ERROR') {
        console.error('❌ Ошибка в silent renew iframe:', event.data.error);
        // Можно добавить дополнительную логику обработки ошибок
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  if (auth.isLoading) {
    return <CircularProgress style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    }} />
  }

  // Если есть ошибка авторизации (пользователь не в группе Gis Users)
  if (authError) {
    return <AuthErrorPage />;
  }

  if (auth.isAuthenticated) {
    return (
      <div className="App" id="App">
        {/* <TokenRefreshHandler /> */}
        {/* <TokenStatus /> */}
        {/* <TasksSync /> */}
        <AppRouter />
        {/* <PerformanceMonitor /> */}
      </div>
    );
  }

  // Если не авторизован, показываем загрузку (перенаправление уже происходит в useEffect)
  return <Riple color="green" size="medium" text="" textColor="" />
}

// // Импортируем утилиты для тестирования редюсеров в development режиме
// if (process.env.NODE_ENV === 'development') {
//   import('./debug/testReducerSwitch.js').then(module => {
//     console.log('🛠️ Debug утилиты загружены');
//   }).catch(err => {
//     console.warn('⚠️ Не удалось загрузить debug утилиты:', err);
//   });
// }

export default App;
